
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import theme from './theme';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import NotFound from './components/NotFound';

// Auth Components
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ForgotPassword from './components/auth/ForgotPassword';
import VerifyOTP from './components/auth/VerifyOTP';
import ChangePassword from './components/auth/ChangePassword';

// Dashboard Components
import CustomerDashboard from './components/CustomerDashboard';
import AdminDashboard from './components/AdminDashboard';
import ProductDetail from './components/ProductDetail';
import ProductsPage from './components/ProductPage';

// Admin Pages
import AdminOverview from './pages/AdminOverview';
import AdminCreatePostSimple from './pages/AdminCreatePostSimple';
import AdminProfile from './pages/AdminProfile';
import AdminCustomers from './pages/AdminCustomers';
import AdminProducts from './pages/AdminProducts';
import AdminRequests from './pages/AdminRequests';
import UserProfile from './pages/UserProfile';
import AdminCoupons from './pages/adminCopans/Admincoupons';
import AdminOrders from './pages/AdminOrders';
import AdminOrderDetail from './pages/AdminOrderDetail';
import AdminCommission from './pages/Admincommission';
import Deliveryaddresses from './pages/customerAddressPage/Deliveryaddresses';
import Payment from './pages/Payment';
import CartPage from './pages/CartPage';

import './App.css';
import 'react-toastify/dist/ReactToastify.css';
import HomePage from './pages/HomePage';

/* ────────────────────────────────────────────────────────────────────────
   Shared loading screen — was duplicated identically in three components
   ──────────────────────────────────────────────────────────────────────── */
const FullscreenLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '18px',
    color: '#bc965e'
  }}>
    Loading...
  </div>
);

/* ────────────────────────────────────────────────────────────────────────
   Guest-accessible pages rendered inside CustomerDashboard.
   Replaces ProductDetailRoute / CartPageRoute / ProductsPageRoute, which
   were three copies of the same wrapper differing only in which page
   component they rendered.
   ──────────────────────────────────────────────────────────────────────── */
const GuestDashboardPage = ({ Component }) => {
  const { loading } = useAuth();

  if (loading) return <FullscreenLoader />;

  return (
    <CustomerDashboard>
      <Component />
    </CustomerDashboard>
  );
};

/* ────────────────────────────────────────────────────────────────────────
   Admin route helper — replaces the repeated
   <ProtectedRoute requireRole="Admin"><AdminDashboard><X /></AdminDashboard></ProtectedRoute>
   nesting nine times over.
   ──────────────────────────────────────────────────────────────────────── */
const AdminRoute = ({ Component }) => (
  <ProtectedRoute requireRole="Admin">
    <AdminDashboard>
      <Component />
    </AdminDashboard>
  </ProtectedRoute>
);

/* ────────────────────────────────────────────────────────────────────────
   Declarative table of admin routes: [path, PageComponent]
   Add a new admin page by adding one line here instead of a whole
   <Route ...> block.
   ──────────────────────────────────────────────────────────────────────── */
const ADMIN_ROUTES = [
  ['/admin/dashboard', AdminOverview],
  ['/admin/create-post', AdminCreatePostSimple],
  ['/admin/profile', AdminProfile],
  ['/admin/customers', AdminCustomers],
  ['/admin/products', AdminProducts],
  ['/admin/requests', AdminRequests],
  ['/admin/coupons', AdminCoupons],
  ['/admin/orders', AdminOrders],
  ['/admin/orders/:billId', AdminOrderDetail],
  ['/admin/commission', AdminCommission],
];

/* ────────────────────────────────────────────────────────────────────────
   Same idea for the guest-accessible customer pages
   ──────────────────────────────────────────────────────────────────────── */
const GUEST_CUSTOMER_ROUTES = [
  ['/', HomePage],
  ['/products', ProductsPage],
  ['/products/:productId', ProductDetail],
  ['/cart', CartPage],
];

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <CartProvider>
          <Router>
            <div className="App">
              <Routes>
                {/* Public Auth Routes - only accessible when NOT logged in */}
                <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
                <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
                <Route path="/verify-otp" element={<PublicRoute><VerifyOTP /></PublicRoute>} />
                <Route path="/change-password" element={<PublicRoute><ChangePassword /></PublicRoute>} />

                {/* Public User Profile Route - accessible to all authenticated users */}
                {/* This must come BEFORE /home/* to avoid being caught by the wildcard */}
                <Route
                  path="/profile/:userId"
                  element={<ProtectedRoute><UserProfile /></ProtectedRoute>}
                />

                {/* Guest-accessible customer pages (products, product detail, cart) -
                    must come BEFORE /home/* so they aren't swallowed by the wildcard */}
                {GUEST_CUSTOMER_ROUTES.map(([path, Component]) => (
                  <Route key={path} path={path} element={<GuestDashboardPage Component={Component} />} />
                ))}

                {/* Protected Routes - require authentication */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute requireRole="Customer">
                      <CustomerDashboard />
                    </ProtectedRoute>
                  }
                />

                {/* Admin Routes */}
                {ADMIN_ROUTES.map(([path, Component]) => (
                  <Route key={path} path={path} element={<AdminRoute Component={Component} />} />
                ))}

                {/* Delivery Address Route */}
                <Route
                  path="/delivery-address"
                  element={<ProtectedRoute requireRole="Customer"><Deliveryaddresses /></ProtectedRoute>}
                />

                <Route
                  path="/payment"
                  element={<ProtectedRoute requireRole="Customer"><Payment /></ProtectedRoute>}
                />

                {/* Default redirect */}
                <Route path="/" element={<Navigate to="/login" replace />} />

                {/* Catch-all route for non-existent paths */}
                <Route path="*" element={<NotFound />} />
              </Routes>

              {/* Toast Container */}
              <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="dark"
                toastStyle={{
                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                  color: '#ffffff',
                  border: '1px solid rgba(188, 150, 94, 0.3)',
                  borderRadius: '12px'
                }}
              />
            </div>
          </Router>
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;